import { useEffect, useMemo, useRef } from 'react';
import QRCodeStyling, { type Options } from 'qr-code-styling';
import { cn } from '@/shared/utils';
import { tv } from 'tailwind-variants';

const qrCodeStyles = tv({
  slots: {
    containerSlot: 'w-[12.2rem] items-end flex',
    qrSlot:
      'flex items-center justify-center w-[12.2rem] h-[12.2rem] bg-background border rounded-[1.6rem]',
  },
});

const defaultQrCodeOptions: Partial<Options> = {
  margin: 0,
  type: 'svg',
  dotsOptions: {
    type: 'dots',
    color: 'currentColor',
  },
  cornersSquareOptions: {
    type: 'rounded',
    color: 'currentColor',
  },
  cornersDotOptions: {
    type: 'dot',
    color: 'currentColor',
  },
  qrOptions: {
    errorCorrectionLevel: 'L',
    typeNumber: 0,
  },
};

interface Props {
  data: string;
  width?: number;
  height?: number;
  className?: string;
  containerClassName?: string;
}

export const QrCode = ({
  data,
  className,
  containerClassName,
  width = 104,
  height = 104,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const { containerSlot, qrSlot } = qrCodeStyles();

  const qrCode = useMemo(() => {
    return new QRCodeStyling({
      ...defaultQrCodeOptions,
      width: width,
      height: height,
      data: data,
    });
  }, [width, height, data]);

  useEffect(() => {
    if (!ref.current) return;

    ref.current.innerHTML = '';
    qrCode.append(ref.current);
  }, [qrCode]);

  return (
    <div className={cn(containerSlot(), className)}>
      <div className={cn(qrSlot(), containerClassName)} ref={ref} />
    </div>
  );
};
